<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import MultiSelect from 'primevue/multiselect'
import Select     from 'primevue/select'
import DatePicker from 'primevue/datepicker'
import Chip       from 'primevue/chip'
import Tag        from 'primevue/tag'
import api from '@/api'
import { countryFromDetails, flagFromISO2, flagSvgUrl, regionNameFromISO2, normalizeISO2 } from '@/utils/country'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { enrichPostRow } from '@/composables/useEnrichPost'

const emit = defineEmits(['filters-change'])

const router = useRouter()
const { t } = useI18n()
const { fmtDate } = useFormatDate()

const posts   = ref([])
const loading = ref(false)
const total   = ref(0)
let   lastQuery = {}

// ── Filter state ──────────────────────────────────────────────────────────────
const countryFilter  = ref([])
const activityFilter = ref([])
const groupFilter    = ref(null)
const dateRange      = ref(null)   // [Date|null, Date|null] | null

// ── Filter options ────────────────────────────────────────────────────────────
const countryOptions  = ref([])
const activityOptions = ref([])
const groupOptions    = ref([])
const optionsLoading  = ref(false)

async function loadFilterOptions() {
  optionsLoading.value = true
  try {
    const [postsOpts, groupsRes] = await Promise.all([
      api.getHackPostsFilterOptions(),
      api.getHackerGroupNames(),
    ])
    countryOptions.value = (postsOpts.data.countries || []).map(c => {
      const info = countryFromCode(c.code)
      return {
        label: info ? `${info.name} (${c.count})` : `${c.code} (${c.count})`,
        flag:  info?.flag ?? '',
        value: c.code,
      }
    })
    activityOptions.value = (postsOpts.data.activities || []).map(a => ({
      label: `${a.name} (${a.count})`,
      value: a.name,
    }))
    groupOptions.value = (groupsRes.data || []).map(g => ({ label: g.name, value: g.id }))
  } catch (e) {
    console.error('Filter options error:', e)
  } finally {
    optionsLoading.value = false
  }
}

function countryFromCode(code) {
  const c = normalizeISO2(code)
  if (!c) return null
  return { flag: flagFromISO2(c), svgUrl: flagSvgUrl(c), code: c, name: regionNameFromISO2(c) || c }
}

// ── Active filter chips ───────────────────────────────────────────────────────
const activeChips = computed(() => {
  const chips = []
  for (const code of countryFilter.value) {
    const info = countryFromCode(code)
    chips.push({ key: 'country', value: code, label: `${t('hackwatch.posts.filterCountryLabel')}: ${info?.name ?? code}` })
  }
  for (const act of activityFilter.value) {
    chips.push({ key: 'activity', value: act, label: `${t('hackwatch.posts.filterIndustryLabel')}: ${act}` })
  }
  if (groupFilter.value) {
    const grp = groupOptions.value.find(g => g.value === groupFilter.value)
    chips.push({ key: 'group', value: groupFilter.value, label: `${t('hackwatch.posts.filterGroupLabel')}: ${grp?.label ?? groupFilter.value}` })
  }
  if (dateRange.value?.[0]) {
    const from = fmtDate(dateRange.value[0])
    const to   = dateRange.value[1] ? fmtDate(dateRange.value[1]) : '…'
    chips.push({ key: 'date', value: 'date', label: `${t('hackwatch.posts.filterDateLabel')}: ${from} – ${to}` })
  }
  return chips
})

const hasAnyFilter = computed(() => activeChips.value.length > 0)

function removeChip(chip) {
  if (chip.key === 'country')  countryFilter.value  = countryFilter.value.filter(v => v !== chip.value)
  if (chip.key === 'activity') activityFilter.value = activityFilter.value.filter(v => v !== chip.value)
  if (chip.key === 'group')    groupFilter.value    = null
  if (chip.key === 'date')     dateRange.value      = null
  onFilterChange()
}

function resetFilters() {
  countryFilter.value  = []
  activityFilter.value = []
  groupFilter.value    = null
  dateRange.value      = null
  onFilterChange()
}

// ── Columns ───────────────────────────────────────────────────────────────────
const columns = [
  { field: 'post_title',        header: t('hackwatch.posts.colTitle'),      sortable: true,  default: true,  minWidth: '200px' },
  { field: 'hacker_group_name', header: t('hackwatch.posts.colGroup'),      sortable: true,  default: true  },
  { field: 'country_code',      header: t('hackwatch.posts.colCountry'),    sortable: true,  default: true  },
  { field: 'domain',            header: t('hackwatch.posts.colDomain'),     sortable: true,  default: true  },
  { field: 'activity',          header: t('hackwatch.posts.colIndustry'),   sortable: true,  default: true  },
  { field: 'attackdate',        header: t('hackwatch.posts.colAttackDate'), sortable: true,  default: false },
  { field: 'discovered',        header: t('hackwatch.posts.colDiscovered'), sortable: true,  default: true  },
]

// ── Helpers ───────────────────────────────────────────────────────────────────
const enrichRow = enrichPostRow

function toISODate(d) {
  if (!d) return undefined
  try { return d instanceof Date ? d.toISOString() : new Date(d).toISOString() } catch { return undefined }
}

// ── Data loading ──────────────────────────────────────────────────────────────
async function fetchPosts(q = {}) {
  lastQuery = q
  loading.value = true
  try {
    const params = {
      limit:    q.limit   ?? 50,
      page:     q.page    ?? 1,
      q:        q.search  || undefined,
      sortBy:   q.sortField || 'discovered',
      sortDir:  q.sortOrder === 'asc' ? 'asc' : 'desc',
      country:  countryFilter.value.length  ? countryFilter.value.join(',')  : undefined,
      activity: activityFilter.value.length ? activityFilter.value.join(',') : undefined,
      group_id: groupFilter.value || undefined,
      dateFrom: toISODate(dateRange.value?.[0]),
      dateTo:   toISODate(dateRange.value?.[1]),
    }
    const { data } = await api.getHackPostsFiltered(params)
    if (data?.rows !== undefined) {
      posts.value = (data.rows || []).map(enrichRow)
      total.value = data.total ?? posts.value.length
    } else if (Array.isArray(data)) {
      posts.value = data.map(enrichRow)
      total.value = posts.value.length
    } else {
      posts.value = []
      total.value = 0
    }
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadFilterOptions()
  fetchPosts()
})

function onQueryChange(q) { fetchPosts(q) }
function onFilterChange()  { fetchPosts({ ...lastQuery, page: 1 }) }

// Mirror active filter state to the stats panel for highlighting chart slices
watch([countryFilter, activityFilter, groupFilter], () => {
  emit('filters-change', {
    country:  countryFilter.value[0] ?? null,
    industry: activityFilter.value[0] ?? null,
    group:    groupFilter.value ?? null,
  })
}, { deep: true })

// Applied from stats panel chart clicks — toggle semantics
function applyExternalFilter({ dimension, value }) {
  if (dimension === 'country') {
    const idx = countryFilter.value.indexOf(value)
    countryFilter.value = idx >= 0 ? [] : [value]
  } else if (dimension === 'industry') {
    const idx = activityFilter.value.indexOf(value)
    activityFilter.value = idx >= 0 ? [] : [value]
  } else if (dimension === 'group') {
    groupFilter.value = groupFilter.value === value ? null : value
  } else {
    return
  }
  onFilterChange()
}

defineExpose({ applyExternalFilter })

function countryFor(row) { return countryFromDetails(row.details) }
function goToGroup(id) { router.push(`/threat-intel/actors/${id}`) }
</script>

<template>
  <div class="posts-overview">

    <!-- ── Filter-Card ─────────────────────────────────────────────────────── -->
    <div class="hw-card filter-card">
      <div class="hw-card-header">
        <i class="pi pi-filter hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.posts.filtersTitle') }}</span>
        <pButton
          v-if="hasAnyFilter"
          icon="pi pi-times"
          :label="t('hackwatch.posts.filterReset')"
          size="small"
          severity="secondary"
          text
          @click="resetFilters"
        />
      </div>

      <div class="filter-grid">
        <!-- Land -->
        <div class="filter-field">
          <label>{{ t('hackwatch.posts.filterCountryLabel') }}</label>
          <MultiSelect
            v-model="countryFilter"
            :options="countryOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.posts.filterCountryPlaceholder')"
            :loading="optionsLoading"
            :max-selected-labels="2"
            :selected-items-label="t('hackwatch.posts.filterCountriesSelected')"
            filter
            show-clear
            class="w-full"
            @change="onFilterChange"
          >
            <template #option="{ option }">
              <span class="filter-option-row">
                <span v-if="option.flag" class="flag">{{ option.flag }}</span>
                <span class="filter-option-label">{{ option.label }}</span>
              </span>
            </template>
          </MultiSelect>
        </div>

        <!-- Branche / Activity -->
        <div class="filter-field">
          <label>{{ t('hackwatch.posts.filterIndustryLabel') }}</label>
          <MultiSelect
            v-model="activityFilter"
            :options="activityOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.posts.filterIndustryPlaceholder')"
            :loading="optionsLoading"
            :max-selected-labels="2"
            :selected-items-label="t('hackwatch.posts.filterIndustriesSelected')"
            filter
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>

        <!-- Hacker-Gruppe -->
        <div class="filter-field">
          <label>{{ t('hackwatch.posts.filterGroupLabel') }}</label>
          <Select
            v-model="groupFilter"
            :options="groupOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.posts.filterGroupPlaceholder')"
            :loading="optionsLoading"
            filter
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>

        <!-- Zeitraum -->
        <div class="filter-field">
          <label>{{ t('hackwatch.posts.filterDateLabel') }}</label>
          <DatePicker
            v-model="dateRange"
            selection-mode="range"
            :placeholder="t('hackwatch.posts.filterDatePlaceholder')"
            date-format="dd.mm.yy"
            show-button-bar
            show-icon
            class="w-full"
            @update:model-value="onFilterChange"
          />
        </div>
      </div>

      <!-- Active chips -->
      <div v-if="hasAnyFilter" class="filter-chips">
        <Chip
          v-for="chip in activeChips"
          :key="chip.key + chip.value"
          :label="chip.label"
          removable
          @remove="removeChip(chip)"
        />
      </div>
    </div>

    <!-- ── Tabelle ─────────────────────────────────────────────────────────── -->
    <AppTable
      table-key="hackwatch-posts"
      :columns="columns"
      :data="posts"
      :total="total"
      :loading="loading"
      lazy
      :default-rows="50"
      :rows-options="[25, 50, 100, 200]"
      data-key="post_id"
      @refresh="fetchPosts(lastQuery)"
      @query-change="onQueryChange"
    >
      <template #cell-post_title="{ row }">
        <router-link :to="`/threat-news/posts/${row.post_id}`" class="hw-link">
          {{ row.post_title || t('hackwatch.posts.untitled') }}
        </router-link>
      </template>

      <template #cell-country_code="{ row }">
        <template v-if="countryFor(row)">
          <img :src="countryFor(row).svgUrl" :alt="countryFor(row).code" class="flag-img" />
          <span class="country-name">{{ countryFor(row).name }}</span>
          <small class="country-code">({{ countryFor(row).code }})</small>
        </template>
        <template v-else-if="row.country_code">{{ row.country_code }}</template>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell-hacker_group_name="{ row }">
        <a href="javascript:void(0)" class="hw-link" @click="goToGroup(row.hacker_groups_id)">
          <Tag :value="row.hacker_group_name || 'N/A'" severity="secondary" />
        </a>
      </template>

      <template #cell-attackdate="{ row }">
        <span v-if="row.attackdate">{{ fmtDate(row.attackdate) }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell-discovered="{ row }">{{ fmtDate(row.discovered) }}</template>
    </AppTable>

  </div>
</template>

<style scoped>
.posts-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hw-link { color: var(--p-text-color); text-decoration: none; }
.hw-link:hover { color: var(--p-primary-color); text-decoration: underline; }
.text-muted { color: var(--p-text-color-secondary); }

.flag-img { width: 20px; height: auto; margin-right: .35rem; vertical-align: middle; border-radius: 2px; }
.country-name { font-weight: 600; }
.country-code { opacity: .65; margin-left: .25rem; }
.flag { font-size: 1.1rem; line-height: 1; }

/* ── Filter-Card ────────────────────────────────────────────────────────────── */
.filter-card { padding-bottom: 12px; }

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 14px;
  padding: 8px 14px 4px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-field label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-color-secondary);
}

.filter-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-option-label { flex: 1; }

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 14px 0;
}
.filter-chips :deep(.p-chip) {
  font-size: 0.78rem;
  padding: 2px 8px;
}
</style>
